import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { startCase } from "lodash";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { ENUM_MS_COMAPNY_POLICIES_STATUS } from "@/utils/enums/company-policies.enum";
import type { IPolicyAcknowledgementFilters } from "@/types/(company)/company-policies.interface";
import { SharedAccordion } from "@/components/common/shared-accordion";

interface Props {
  filters: IPolicyAcknowledgementFilters;
  onChange: (newFilters: IPolicyAcknowledgementFilters) => void;
}

// --- Main Component ---
const PolicyAcknowledgementFilters = ({ filters, onChange }: Props) => {
  const { t } = useTranslation(["common", "company"]);
  const handleClear = (
    key: keyof IPolicyAcknowledgementFilters,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    onChange({ ...filters, [key]: "" });
  };

  return (
    <div>
      <SharedAccordion
        type="multiple"
        expanded
        items={[
          {
            label: t("company:policies_settings.viewed_on"),
            content: (
              <div className="w-full flex gap-2">
                <DatePickerInput
                  label={t("company:policies_settings.from_label")}
                  value={filters?.viewedOnFrom || new Date()}
                  onChange={(val) =>
                    onChange({ ...filters, viewedOnFrom: val || null })
                  }
                  containerStyle="flex-1"
                />
                <DatePickerInput
                  label={t("company:policies_settings.to_label")}
                  value={filters?.viewedOnTo || new Date()}
                  onChange={(val) =>
                    onChange({ ...filters, viewedOnTo: val || null })
                  }
                  containerStyle="flex-1"
                />
              </div>
            ),
            suffix: (
              <ClearButton
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({
                    ...filters,
                    viewedOnFrom: new Date(),
                    viewedOnTo: new Date(),
                  });
                }}
              />
            ),
          },
          {
            label: t("company:policies_settings.acknowledged_on"),
            content: (
              <div className="w-full flex gap-2">
                <DatePickerInput
                  label={t("company:policies_settings.from_label")}
                  value={filters?.acknowledgedOnFrom || new Date()}
                  onChange={(val) =>
                    onChange({ ...filters, acknowledgedOnFrom: val || null })
                  }
                  containerStyle="flex-1"
                />
                <DatePickerInput
                  label={t("company:policies_settings.to_label")}
                  value={filters?.acknowledgedOnTo || new Date()}
                  onChange={(val) =>
                    onChange({ ...filters, acknowledgedOnTo: val || null })
                  }
                  containerStyle="flex-1"
                />
              </div>
            ),
            suffix: (
              <ClearButton
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({
                    ...filters,
                    acknowledgedOnFrom: new Date(),
                    acknowledgedOnTo: new Date(),
                  });
                }}
              />
            ),
          },
          {
            label: t("common:labels.status"),
            content: (
              <FilterSelect
                label={t("common:labels.status")}
                placeholder={t("company:shared.select_status")}
                value={filters.statusLabel}
                onValueChange={(val) =>
                  onChange({ ...filters, statusLabel: val })
                }
              >
                <SelectItem value="all">{t("common:shared_ui.all")}</SelectItem>
                {Object.values(ENUM_MS_COMAPNY_POLICIES_STATUS).map(
                  (status) => (
                    <SelectItem key={status} value={status}>
                      {startCase(status)}
                    </SelectItem>
                  ),
                )}
              </FilterSelect>
            ),
            suffix: (
              <ClearButton onClick={(e) => handleClear("statusLabel", e)} />
            ),
          },
        ]}
      />
    </div>
  );
};

export default PolicyAcknowledgementFilters;

// --- Sub-components  ---

const ClearButton = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void;
}) => {
  const { t } = useTranslation(["common", "company"]);

  return (
    <Button
      variant="link"
      size="sm"
      className="text-sm text-red-500 font-normal underline"
      onClick={onClick}
    >
      {t("common:buttons.clear")}
    </Button>
  );
};

interface FilterSelectProps {
  placeholder: string;
  label: string;
  value?: string;
  onValueChange: (val: string) => void;
  onOpenChange?: () => void;
  children: React.ReactNode;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
}

const FilterSelect = ({
  placeholder,
  label,
  value,
  onValueChange,
  onOpenChange,
  children,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isLoading,
}: FilterSelectProps) => {
  const { t } = useTranslation(["common", "company"]);

  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      onOpenChange={onOpenChange}
    >
      <SelectTrigger className="h-min w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup onPointerDown={(e) => e.stopPropagation()}>
          <SelectLabel>{label}</SelectLabel>

          {isLoading ? (
            <SelectItem value="loading" disabled>
              {t("company:policies_settings.loading")}
            </SelectItem>
          ) : (
            <>
              {children}

              {hasNextPage && (
                <div className="p-1 border-t mt-1">
                  <button
                    className="text-xs text-primary font-medium"
                    disabled={isFetchingNextPage}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onLoadMore?.();
                    }}
                  >
                    {isFetchingNextPage
                      ? t("company:policies_settings.loading")
                      : t("company:policies_settings.load_more")}
                  </button>
                </div>
              )}
            </>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
